import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { loadAiMessages } from "@/lib/ai-assistant.functions";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, Sparkles, Wrench, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { z } from "zod";

export const Route = createFileRoute("/_authenticated/admin/ai/$threadId")({
  validateSearch: z.object({ q: z.string().optional() }),
  component: ChatPage,
});

const SUGGESTIONS = [
  "How's the day looking? Booking summary.",
  "Which bookings are pending approval?",
  "Sales analysis for the last 7 days and 3 tips to boost revenue.",
  "What are the next 3 events and how to promote them better?",
];

function ChatPage() {
  const { threadId } = Route.useParams();
  const { q } = Route.useSearch();
  const loadFn = useServerFn(loadAiMessages);

  const { data: initial, isLoading } = useQuery({
    queryKey: ["ai-messages", threadId],
    queryFn: () => loadFn({ data: { threadId } }),
  });

  if (isLoading || !initial) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  return <Chat threadId={threadId} initialMessages={initial as unknown as UIMessage[]} autoPrompt={q} />;
}

function Chat({ threadId, initialMessages, autoPrompt }: { threadId: string; initialMessages: UIMessage[]; autoPrompt?: string }) {
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/admin/ai-chat",
        fetch: async (input, init) => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          const headers = new Headers(init?.headers);
          if (token) headers.set("Authorization", `Bearer ${token}`);
          return fetch(input, { ...init, headers });
        },
        prepareSendMessagesRequest: ({ messages, body }) => ({
          body: { messages, threadId, ...body },
        }),
      }),
    [threadId],
  );

  const { messages, sendMessage, status, error } = useChat({
    id: threadId,
    messages: initialMessages,
    transport,
  });

  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, status]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [threadId, status]);

  const nav = useNavigate();
  const autoSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoPrompt) return;
    if (autoSentRef.current === autoPrompt) return;
    if (initialMessages.length > 0) return;
    autoSentRef.current = autoPrompt;
    void sendMessage({ text: autoPrompt });
    // clear the search param so refresh doesn't resend
    nav({
      to: "/admin/ai/$threadId",
      params: { threadId },
      search: {},
      replace: true,
    });
  }, [autoPrompt, initialMessages.length, sendMessage, nav, threadId]);

  const busy = status === "submitted" || status === "streaming";

  async function submit() {
    const trimmed = input.trim();
    if (!trimmed || busy) return;
    setInput("");
    await sendMessage({ text: trimmed });
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Sparkles className="h-4 w-4 text-primary" />
        <h1 className="text-sm font-semibold">Plunkets AI Manager</h1>
        <span className="ml-auto text-xs text-muted-foreground">
          Bookings · Events · Sales Strategy
        </span>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {messages.length === 0 && (
          <div className="mx-auto max-w-2xl space-y-4 py-8">
            <div className="text-center">
              <Sparkles className="mx-auto h-8 w-8 text-primary" />
              <h2 className="mt-3 font-display text-lg">How can I help today?</h2>
              <p className="text-sm text-muted-foreground">
                Ask about bookings, events or request sales strategy tips.
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => sendMessage({ text: s })}
                  className="rounded-lg border border-border p-3 text-left text-sm hover:border-primary/50 hover:bg-muted"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}

        {busy && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Thinking…
          </div>
        )}

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
            {error.message}
          </div>
        )}
      </div>

      <div className="border-t border-border p-3">
        <div className="mx-auto flex max-w-3xl items-end gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Ask something… (Enter to send)"
            rows={2}
            className="min-h-[52px] resize-none"
          />
          <Button onClick={submit} disabled={busy || !input.trim()} size="icon" className="h-11 w-11">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: UIMessage }) {
  const isUser = message.role === "user";
  const text = message.parts
    .filter((p) => p.type === "text")
    .map((p) => (p as { text: string }).text)
    .join("\n");
  const toolParts = message.parts.filter((p) => p.type.startsWith("tool-"));

  return (
    <div className={cn("mx-auto flex max-w-3xl gap-3", isUser && "flex-row-reverse")}>
      <div
        className={cn(
          "grid h-7 w-7 shrink-0 place-items-center rounded-full border",
          isUser ? "border-primary/40 bg-primary/10 text-primary" : "border-border bg-muted",
        )}
      >
        {isUser ? <User className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
      </div>
      <div className={cn("flex min-w-0 flex-1 flex-col gap-2", isUser && "items-end")}>
        {toolParts.map((p, i) => (
          <ToolCard key={i} part={p as ToolPart} />
        ))}
        {text && (
          <div
            className={cn(
              "rounded-lg px-3 py-2 text-sm",
              isUser
                ? "bg-primary text-primary-foreground"
                : "prose prose-sm prose-invert max-w-none text-foreground",
            )}
          >
            {isUser ? text : <ReactMarkdown>{text}</ReactMarkdown>}
          </div>
        )}
      </div>
    </div>
  );
}

type ToolPart = {
  type: string;
  state?: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
};

function ToolCard({ part }: { part: ToolPart }) {
  const name = part.type.replace(/^tool-/, "");
  const done = part.state === "output-available";
  return (
    <details className="rounded-md border border-border bg-muted/40 text-xs">
      <summary className="flex cursor-pointer items-center gap-2 px-3 py-1.5">
        <Wrench className="h-3 w-3 text-primary" />
        <span className="font-mono">{name}</span>
        <span className="ml-auto text-muted-foreground">
          {done ? "done" : part.state ?? "…"}
        </span>
      </summary>
      <div className="space-y-2 border-t border-border px-3 py-2">
        {part.input != null && (
          <div>
            <p className="text-muted-foreground">input</p>
            <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all">
              {JSON.stringify(part.input, null, 2)}
            </pre>
          </div>
        )}
        {part.output != null && (
          <div>
            <p className="text-muted-foreground">output</p>
            <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-all">
              {JSON.stringify(part.output, null, 2)}
            </pre>
          </div>
        )}
        {part.errorText && <p className="text-destructive">{part.errorText}</p>}
      </div>
    </details>
  );
}
